import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { withToastManager } from 'react-toast-notifications';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Tooltip } from '@material-ui/core';
import moment from 'moment';
import { notificationItemsActions, userNotificationSettingActions } from '../redux/actions';
import MixedView from './MixedView';
import MobileView from './MobileView';
import { CardLoader } from './Loader';
import { nsOptions } from '../i18n';
import {
  Table, TableHead, TableBody, TableRow, TableCell,
} from './CustomTable';
import {
  PENDING, SENT,
} from '../constants';
import FormattedValue from '../utils/FormattedValue';
import LabelStatus from './LabelStatus';
import ErrorUtil from '../utils/ErrorUtil';
import Toast from '../utils/Toast';

const mapStateToProps = (state) => ({
  notificationItems: state.notificationItems || {},
  user: state?.auth?.authUser || {},
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  fetchNotificationItems: async (notificationId, offset = 0) => dispatch(
    notificationItemsActions.list({
      notification: notificationId,
      limit: 10,
      offset,
      ordering: 'inclusion__per_project_id',
      admin: ownProps.admin,
    }),
  ),
  fetchNotificationSettings: async (notificationId, userId) => (
    dispatch(userNotificationSettingActions.list({
      notification: notificationId,
      user: userId,
      admin: ownProps.admin,
    }))
  ),
  handleResend: async (notificationItemId, updatedData) => (
    dispatch(notificationItemsActions.patch(notificationItemId, updatedData))
  ),
});

@withToastManager
@connect(mapStateToProps, mapDispatchToProps)
@withTranslation('', nsOptions)
class NotificationsRecipientsTable extends React.Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    notificationToDisplay: PropTypes.shape().isRequired,
    fetchNotificationItems: PropTypes.func.isRequired,
    fetchNotificationSettings: PropTypes.func.isRequired,
    handleResend: PropTypes.func.isRequired,
    user: PropTypes.shape({
      language: PropTypes.string,
    }),
  };

  static defaultProps = {
    user: {
      language: 'en',
    },
  };

  constructor(props) {
    super(props);
    this.state = {
      recipients: [],
      notificationsHistory: new Map(),
      loading: false,
      isLoadingMore: false,
      nextPageUrl: null,
      notificationSettings: {},
    };
  }

  componentDidMount() {
    if (this.props.notificationToDisplay) {
      this.loadFirstPage();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.notificationToDisplay?.id !== this.props.notificationToDisplay?.id) {
      this.loadFirstPage();
    }
  }

  getNotifSettingInclusions = (notifItem) => {
    const { notificationSettings } = this.state;
    if (notificationSettings[notifItem.inclusion.creator]) {
      const settings = notificationSettings[notifItem.inclusion.creator];
      if (settings && settings.notification_setting_inclusions) {
        const inclusion = settings.notification_setting_inclusions.find(
          (inc) => inc.inclusion.id === notifItem.inclusion.id,
        );
        return inclusion || null;
      }
    }
    return null;
  };

  getStatus = (item) => {
    const notifSettings = this.getNotifSettingInclusions(item);
    if (notifSettings) {
      return notifSettings.unsubscribe_status || notifSettings.status || 'ongoing';
    }
    return item.status === 'inactive' ? 'ongoing' : item.status;
  };

  loadFirstPage = async () => {
    this.setState({
      loading: true,
      recipients: [],
      notificationsHistory: new Map(),
      nextPageUrl: null,
      notificationSettings: {},
    });

    try {
      const {
        notificationToDisplay,
        fetchNotificationItems,
        fetchNotificationSettings,
      } = this.props;
      const data = await fetchNotificationItems(notificationToDisplay.id);

      const notificationsHistory = new Map();
      data.results.forEach((item) => {
        const key = item.inclusion.per_project_id;
        if (!notificationsHistory.has(key)) {
          notificationsHistory.set(key, []);
        }
        notificationsHistory.get(key).push(item);
      });

      const uniqueRecipients = data.results ? [...new Map(
        data.results.map((item) => {
          const mappedItem = { ...item };
          return [item.inclusion.per_project_id, mappedItem];
        }),
      ).values()] : [];

      uniqueRecipients.sort((a, b) => {
        const aId = parseInt(a.inclusion.per_project_id, 10);
        const bId = parseInt(b.inclusion.per_project_id, 10);
        return aId - bId;
      });

      const creators = new Set(data.results.map((item) => item.inclusion.creator));
      const settingsPromises = Array.from(creators).map((creatorId) => fetchNotificationSettings(
        notificationToDisplay.id,
        creatorId,
      ));
      const settingsResults = await Promise.all(settingsPromises);

      const notificationSettings = {};
      Array.from(creators).forEach((creatorId, index) => {
        if (settingsResults[index].results.length > 0) {
          [notificationSettings[creatorId]] = settingsResults[index].results;
        }
      });

      this.setState({
        loading: false,
        recipients: uniqueRecipients,
        notificationsHistory,
        nextPageUrl: data.next,
        notificationSettings,
      });
    } catch (error) {
      this.setState({ loading: false });
      ErrorUtil.handleCatched(this.props, error, false);
    }
  };

  loadNextPage = async () => {
    const {
      nextPageUrl,
      recipients,
      isLoadingMore,
      notificationSettings,
    } = this.state;
    const { fetchNotificationItems, fetchNotificationSettings, notificationToDisplay } = this.props;

    if (!nextPageUrl || isLoadingMore) return;

    this.setState({ isLoadingMore: true });

    try {
      const offset = recipients.length;
      const data = await fetchNotificationItems(notificationToDisplay.id, offset);

      const newCreators = new Set(data.results
        .filter((item) => !notificationSettings[item.inclusion.creator])
        .map((item) => item.inclusion.creator));

      if (newCreators.size > 0) {
        const settingsPromises = Array.from(newCreators).map((creatorId) => (
          fetchNotificationSettings(notificationToDisplay.id, creatorId)
        ));
        const settingsResults = await Promise.all(settingsPromises);

        const newSettings = { ...notificationSettings };
        Array.from(newCreators).forEach((creatorId, index) => {
          if (settingsResults[index].results.length > 0) {
            [newSettings[creatorId]] = settingsResults[index].results;
          }
        });

        this.setState({ notificationSettings: newSettings });
      }

      const allRecipients = [...recipients];
      const newRecipients = data.results || [];

      newRecipients.forEach((item) => {
        const isDuplicate = allRecipients.some((existing) => (
          existing.inclusion.per_project_id === item.inclusion.per_project_id
        ));
        if (!isDuplicate) {
          const mappedItem = { ...item };
          allRecipients.push(mappedItem);
        }
      });

      allRecipients.sort((a, b) => {
        const aId = parseInt(a.inclusion.per_project_id, 10);
        const bId = parseInt(b.inclusion.per_project_id, 10);
        return aId - bId;
      });

      this.setState({
        recipients: allRecipients,
        nextPageUrl: data.next,
        isLoadingMore: false,
      });
    } catch (error) {
      this.setState({ isLoadingMore: false });
      ErrorUtil.handleCatched(this.props, error, false);
    }
  };

  resend = async (notificationItem) => {
    const { handleResend } = this.props;
    try {
      handleResend(notificationItem.id, {
        resend: true,
      });
      Toast.success(this.props, 'project:notification.table.sent');
    } catch (error) {
      ErrorUtil.handleCatched(this.props, error);
    }
  }

  multipleLineTooltipContent = (items) => {
    const { t, user } = this.props;
    const sentItems = items.filter((item) => item.status === SENT);
    const plannedItems = items.filter((item) => item.status === PENDING);

    const sentItemsDOM = (sentItems.length === 0 ? (
      <span className="ml-2">{t('project:notification.table.nothing-sent')}</span>
    ) : (
      <ul>
        {sentItems.map((item) => (
          <li key={item.id}>{moment(item.timestamp).locale(user.language || 'en').format('L')}</li>
        ))}
      </ul>
    ));

    const plannedItemsDOM = (plannedItems.length === 0 ? (
      <span className="ml-2">{t('project:notification.table.nothing-planned')}</span>
    ) : (
      <ul>
        {plannedItems.map((item) => (
          <li key={item.id}>{moment(item.timestamp).locale(user.language || 'en').format('L')}</li>
        ))}
      </ul>
    ));

    return (
      <div>
        <p>{t('project:notification.table.sending-dates')}</p>
        {sentItemsDOM}
        {plannedItems.length > 0 && (
          <>
            <br />
            <p className="mt-3">{`${t('project:notification.table.next-notification')} :`}</p>
            {plannedItemsDOM}
          </>
        )}
      </div>
    );
  }

  render() {
    const {
      loading,
      isLoadingMore,
      recipients,
      nextPageUrl,
    } = this.state;
    const { t } = this.props;

    if (loading) {
      return (
        <div className="text-center">
          <CardLoader />
        </div>
      );
    }

    const notificationItemsDOM = recipients.map((notifItem) => {
      const status = this.getStatus(notifItem);
      const notifSettings = this.getNotifSettingInclusions(notifItem);
      const isUnsubscribed = notifSettings && (
        notifSettings.unsubscribe_status === 'acknowledged'
        || notifSettings.unsubscribe_status === 'stopped'
      );

      return (
        <MixedView key={notifItem.id}>
          <TableRow style={{ height: '67px' }}>
            <TableCell role="presentation">
              <FormattedValue value={notifItem.inclusion.per_project_id} />
            </TableCell>
            <TableCell role="presentation">
              <Tooltip title={notifItem.inclusion.creator_email} placement="top">
                <span>{notifItem.inclusion.creator_name}</span>
              </Tooltip>
            </TableCell>
            <TableCell role="presentation">
              <Tooltip
                title={this.multipleLineTooltipContent(
                  this.state.notificationsHistory.get(notifItem.inclusion.per_project_id) || [],
                )}
                placement="top"
              >
                <span>
                  <FontAwesomeIcon icon={['fas', 'history']} size="sm" />
                </span>
              </Tooltip>
            </TableCell>
            <TableCell role="presentation">
              {isUnsubscribed && notifSettings ? (
                <Tooltip
                  title={notifSettings.user_who_updated_status_email}
                  placement="top"
                >
                  <span>
                    <LabelStatus
                      center
                      status={status}
                    />
                  </span>
                </Tooltip>
              ) : (
                <LabelStatus
                  center
                  status={status}
                />
              )}
            </TableCell>
            <TableCell role="presentation">
              <Button
                className=""
                variant="contained"
                onClick={() => this.resend(notifItem)}
                onKeyPress={() => {}}
                onTouchEnd={() => {}}
                size="small"
                color="primary"
              >
                {t('project:notification.table.resend')}
              </Button>
            </TableCell>
          </TableRow>
        </MixedView>
      );
    });

    return (
      <div className="contains-loader">
        {loading && <CardLoader />}
        <MixedView>
          <div>
            <Table extraClassName="mt-3 mb-5 no-pointer">
              <TableHead>
                <TableRow>
                  <TableCell tag="th">
                    {t('project:notification.table.inclusion-id')}
                  </TableCell>
                  <TableCell tag="th">
                    {t('project:notification.table.recipient')}
                  </TableCell>
                  <TableCell tag="th">
                    {t('project:notification.table.notifications')}
                  </TableCell>
                  <TableCell tag="th">
                    {t('project:notification.table.progression')}
                  </TableCell>
                  <TableCell tag="th">
                    {t('project:notification.table.action')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {notificationItemsDOM.length > 0 ? notificationItemsDOM : (
                  <TableRow key="no-one" className="no-one">
                    <TableCell colSpan="8">
                      {t('project:no.recipient')}
                    </TableCell>
                  </TableRow>
                )}
                {isLoadingMore && (
                  <TableRow key="loader-row">
                    <TableCell colSpan="8" style={{ textAlign: 'center' }}>
                      <CardLoader />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </MixedView>
        <MobileView>
          <div className="mt-3 mb-0">
            {notificationItemsDOM.length > 0 ? notificationItemsDOM : (
              <div>
                <div className="align-middle text-center no-notification">
                  {t('project:no.recipient')}
                </div>
              </div>
            )}
          </div>
        </MobileView>
        {isLoadingMore && (
          <div className="text-center my-3">
            <i className="fa fa-spinner fa-spin" />
            <span className="ml-2">{t('loading_more')}</span>
          </div>
        )}

        {nextPageUrl && !isLoadingMore && (
          <div className="text-center my-3">
            <button
              className="btn btn-primary"
              onClick={this.loadNextPage}
            >
              {t('load_more')}
            </button>
          </div>
        )}
      </div>
    );
  }
}


export default NotificationsRecipientsTable;
